﻿<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <RockFormField :modelValue="internalValue"
                   v-bind="formFieldProps"
                   @change="onChange"
                   formGroupClasses="rock-phonenumber-box"
                   :rules="rules"
                   name="phonenumberbox">
        <template #default="{ uniqueId, field }">
            <div class="control-wrapper">
                <div class="input-group phone-number-box" :class="inputGroupClasses">
                    <span v-if="availableCountryCodeOptions.length < 2" class="input-group-addon">
                        <i class="fa fa-phone-square"></i>
                    </span>
                    <InputGroupDropDown v-else :items="availableCountryCodeOptions" v-model="countryCode" inputGroupClasses="input-group-addon" />
                    <input v-model="internalValue" :id="uniqueId" type="text" class="form-control" v-bind="field" />
                </div>
            </div>
        </template>
    </RockFormField>
</template>

<script lang="ts" setup>
    import { computed, PropType, ref, watch } from "vue";
    import RockFormField from "./rockFormField.obs";
    import { PhoneNumberBoxGetConfigurationResultsBag } from "@Obsidian/ViewModels/Rest/Controls/phoneNumberBoxGetConfigurationResultsBag";
    import { PhoneNumberCountryCodeRulesConfigurationBag } from "@Obsidian/ViewModels/Rest/Controls/phoneNumberCountryCodeRulesConfigurationBag";
    import { standardRockFormFieldProps, useStandardRockFormFieldProps, useVModelPassthrough } from "@Obsidian/Utility/component";
    import { normalizeRules } from "@Obsidian/ValidationRules";
    import { getPhoneNumberConfiguration, formatPhoneNumber, stripPhoneNumber } from "@Obsidian/Utility/phone";
    import { ListItemBag } from "@Obsidian/ViewModels/Utility/listItemBag";
    import InputGroupDropDown from "./inputGroupDropDown.obs";

    const props = defineProps({
        /** v-model of Phone Number */
        modelValue: {
            type: String as PropType<string>,
            default: ""
        },

        /** v-model of Country Code */
        countryCode: {
            type: String as PropType<string>,
            default: ""
        },

        inputGroupClasses: {
            type: String as PropType<string>,
            default: ""
        },

        ...standardRockFormFieldProps
    });

    const emit = defineEmits<{
        (e: "update:modelValue", value: string): void
        (e: "update:countryCode", value: string): void
    }>();

    // #region Values

    const internalValue = ref(props.modelValue);
    const countryCode = useVModelPassthrough(props, "countryCode", emit);
    const formFieldProps = useStandardRockFormFieldProps(props);
    const configuration = ref<PhoneNumberBoxGetConfigurationResultsBag | null>(null);

    // #endregion

    // #region Computed Values

    /** The rules for the specified country code */
    const configuredRules = computed<PhoneNumberCountryCodeRulesConfigurationBag[]>(() => {
        return configuration.value?.rules?.[countryCode.value] ?? [];
    });

    /** Validation Rules */
    const rules = computed(() => {
        const rules = normalizeRules(props.rules);
        rules.push(validatePhoneNumber);
        return rules;
    });

    const availableCountryCodes = computed<string[]>(() => {
        return configuration.value?.countryCodes ?? [];
    });

    const availableCountryCodeOptions = computed<ListItemBag[]>(() => {
        if (availableCountryCodes.value.length == 0) {
            return [{ value: "1", text: "1" }];
        }

        return availableCountryCodes.value.map((val) => ({ value: val, text: val }));
    });

    // #endregion

    // #region Functions

    /** Uses the configuration we received from the server to determine whether the entered number is valid */
    function validatePhoneNumber(value: unknown): string | boolean {
        const rules = configuredRules.value;

        if (!value) {
            return true;
        }

        const val = stripPhoneNumber(value as string);

        if (rules.length == 0) {
            return true;
        }

        for (let rule of rules) {
            const regex = new RegExp(rule.match ?? "");

            if (regex.test(val)) {
                return true;
            }
        }

        return `The phone number '${value}' is not a valid phone number.`;
    }

    /** Retrieve the country code data, validation data, and formatting data from the server */
    async function loadConfiguration(): Promise<void> {
        try {
            const result = await getPhoneNumberConfiguration();

            if (result) {
                configuration.value = result;
                countryCode.value ||= result.defaultCountryCode ?? "";
            }
            else {
                console.error("Unknown error while loading phone number configuration.");
            }
        }
        catch (e) {
            console.error(e ?? "Unknown error while loading phone number configuration.");
        }
    }

    // #endregion

    // #region Event Handlers

    function onChange(): void {
        internalValue.value = formatPhoneNumber(stripPhoneNumber(internalValue.value), configuredRules.value);
    }

    // #endregion

    // #region Watchers

    watch(internalValue, () => {
        // The value that should be stored for phone number attribute values is the formatted version.
        // This seems backwards, but actually keeps parity with the web forms functionality.
        emit("update:modelValue", formatPhoneNumber(stripPhoneNumber(internalValue.value), configuredRules.value));
    }, { immediate: true });

    watch(() => props.modelValue, () => {
        internalValue.value = formatPhoneNumber(stripPhoneNumber(props.modelValue), configuredRules.value);
    }, { immediate: true });

    watch(configuredRules, () => {
        internalValue.value = formatPhoneNumber(stripPhoneNumber(internalValue.value), configuredRules.value);
    });

    watch([configuration, availableCountryCodes], () => {
        // If we don't have any country codes from the server, ignore
        if (configuration.value == null || availableCountryCodes.value.length == 0) return;

        // If no country code set or the set country code isn't in the list of available codes, then change it to the default
        if (countryCode.value == null || !availableCountryCodes.value.includes(countryCode.value)) {
            countryCode.value = availableCountryCodes.value[0];
        }
    });

    // #endregion

    loadConfiguration();

</script>